import { Box, Grid, IconButton, Skeleton, Tooltip, Typography } from '@mui/material';
import React, { ReactNode, useEffect, useState } from 'react';
import './technical-indicators.ui.scss';
import { getTechnicalIndicators } from '../../../services/technical-indicators.service';
import { showAxiosMessageError } from '../../../utils/error-message.util';
import { useSnackbars } from '../../../hooks/snackbar.hook';
import { TechnicalIndicatorDto, TypeTechnicalIndicator } from '../../../services/dtos/technical-indicator.dto';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import EuroIcon from '@mui/icons-material/Euro';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import GetAppIcon from '@mui/icons-material/GetApp';
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import GroupsIcon from '@mui/icons-material/Groups';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import { BarChart, LineChart } from '@mui/x-charts';
import { MandateGeneralDto } from '../../../services/dtos/mandate-general.dto';
import { MandateEstablishmentDto } from '../../../services/dtos/mandate-establishment-dto';
import { MandateTerritoryDto } from '../../../services/dtos/mandate-territory.dto';
import { getSubtitle } from '../../header/header';
import { getValueLocalStorage } from '../../../hooks/use-local-storage.hook';
import { useLocation } from 'react-router-dom';
import { UserAccountDto } from '../../../services/dtos/user-account.dto';

type TechnicalIndicatorPdfState = {
    graph: TechnicalIndicatorDto;
    mandate?: MandateGeneralDto | MandateEstablishmentDto | MandateTerritoryDto;
    title: string | null;
};

/**
 * Vue pour l'affichage de l'onglet indicateurs techniques.
 * @param props props de l'ihm
 * @constructor
 */
export default function TechnicalIndicatorsDataUi(
    props: Readonly<{
        mandate?: MandateGeneralDto | MandateEstablishmentDto | MandateTerritoryDto;
        type: string;
        title: boolean;
    }>
) {
    const showMessage = useSnackbars();
    const location = useLocation();
    const [technicalIndicators, setTechnicalIndicators] = useState<Array<TechnicalIndicatorDto>>();

    const chartHeight = 220;
    const sectionTitle = props.type === 'assainissement' ? 'Assainissement collectif' : 'Eau potable';

    const navigateExternal = (target: string | URL | undefined, options: { state: TechnicalIndicatorPdfState }) => {
        if (options.state) {
            localStorage.setItem('state', JSON.stringify(options.state));
        }
        globalThis.open(target, '_blank', 'noreferrer');
    };

    useEffect(() => {
        let isMounted = true;
        if (props.mandate) {
            getTechnicalIndicators(props.mandate.id, props.type)
                .then(result => {
                    if (isMounted) {
                        setTechnicalIndicators(result);
                    }
                })
                .catch(error => {
                    console.error('Erreur lors de la récupération des indicateurs techniques');
                    showAxiosMessageError(error, showMessage);
                });
        }

        return () => {
            isMounted = false;
        };
    }, [props.mandate, props.type, showMessage]);

    const downloadPDFWithChart = (event: React.MouseEvent<HTMLButtonElement>, value: TechnicalIndicatorDto) => {
        event.stopPropagation();
        event.preventDefault();

        navigateExternal('/elu/generer-pdf', {
            state: {
                graph: value,
                mandate: props.mandate,
                title: getSubtitle(
                    location.pathname,
                    props.mandate ? props.mandate.id : null,
                    props.mandate && 'establishmentId' in props.mandate ? props.mandate.establishmentId ?? null : null,
                    getValueLocalStorage('userAccount') as UserAccountDto
                ),
            },
        });
    };

    return (
        <Grid
            container
            spacing={2}
            className={`technical-indicators__section technical-indicators__section--${props.type}`}
        >
            {props.title && (
                <Grid item xs={12}>
                    <Typography className={'technical-indicators__sectionTitle'}>{sectionTitle}</Typography>
                </Grid>
            )}

            {technicalIndicators === undefined &&
                [0, 1, 2].map(index => (
                    <Grid item xs={12} sm={6} lg={4} key={`indicator-loading-${props.type}-${index}`}>
                        <Box className={'technical-indicators__kpiCard technical-indicators__kpiCard--loading'}>
                            <Skeleton variant="text" width={'50%'} height={28} />
                            <Skeleton variant="text" width={'35%'} height={46} />
                            <Skeleton variant="rounded" height={150} />
                        </Box>
                    </Grid>
                ))}

            {technicalIndicators?.length === 0 && (
                <Grid item xs={12}>
                    <Box className={'technical-indicators__emptyState'}>
                        <Typography className={'technical-indicators__emptyTitle'}>
                            Aucune donnée technique disponible
                        </Typography>
                        <Typography className={'technical-indicators__emptyDescription'}>
                            Les indicateurs réels ne remontent pas encore pour cette compétence dans l’environnement
                            actuel.
                        </Typography>
                    </Box>
                </Grid>
            )}

            {technicalIndicators?.map((value, index) => {
                const indicatorMeta = getTechnicalIndicatorMeta(value.type);
                const comparisonChartData =
                    value.type === TypeTechnicalIndicator.COMPARETHEORETICALREAL
                        ? getComparisonChartData(value.values)
                        : null;

                return (
                    <Grid item xs={12} sm={6} lg={4} key={`${props.type}-${value.id}-${index}`}>
                        <Box className={'technical-indicators__kpiCard'}>
                            <Box className={'technical-indicators__kpiHeader'}>
                                <Box className={'technical-indicators__kpiTitleWrap'}>
                                    <Box className={'technical-indicators__kpiIcon'}>{indicatorMeta.icon}</Box>
                                    <Typography className={'technical-indicators__kpiTitle'}>
                                        {indicatorMeta.label}
                                    </Typography>
                                    <Box className={'technical-indicators__kpiMetric'}>
                                        <Typography className={'technical-indicators__kpiValue'}>
                                            {value.currentValue}
                                        </Typography>
                                        <Typography className={'technical-indicators__kpiUnit'}>
                                            {value.unit}
                                        </Typography>
                                    </Box>
                                </Box>

                                {value.values && (
                                    <Tooltip title={'Exporter le graphique'} arrow>
                                        <IconButton
                                            className={'technical-indicators__downloadButton'}
                                            data-testid={`get-file-${value.id}`}
                                            onClick={event => downloadPDFWithChart(event, value)}
                                        >
                                            <GetAppIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>

                            {value.values ? (
                                <Box className={'technical-indicators__chartShell'}>
                                    {value.type === TypeTechnicalIndicator.COMPARETHEORETICALREAL &&
                                        comparisonChartData && (
                                            <BarChart
                                                height={chartHeight}
                                                xAxis={[
                                                    {
                                                        data: comparisonChartData.labels,
                                                        scaleType: 'band',
                                                        label: 'Date',
                                                    },
                                                ]}
                                                series={[
                                                    {
                                                        data: comparisonChartData.real,
                                                        label: getLabelChart(value.type),
                                                        color: getColorChart(value.type),
                                                    },
                                                    {
                                                        data: comparisonChartData.theoretical,
                                                        label: getLabelChart(value.type, 'theorique'),
                                                        color: getColorChart(value.type, 'theorique'),
                                                    },
                                                ]}
                                            />
                                        )}

                                    {value.type === TypeTechnicalIndicator.RECIPEEXCLUDINGTAX && (
                                        <BarChart
                                            height={chartHeight}
                                            xAxis={[
                                                {
                                                    data: value.values.map(currentValue => currentValue.label),
                                                    scaleType: 'band',
                                                    label: 'Date',
                                                },
                                            ]}
                                            series={[
                                                {
                                                    data: value.values.map(currentValue =>
                                                        Number.parseFloat(currentValue.value)
                                                    ),
                                                    label: getLabelChart(value.type),
                                                    color: getColorChart(value.type),
                                                },
                                            ]}
                                        />
                                    )}

                                    {value.type !== TypeTechnicalIndicator.RECIPEEXCLUDINGTAX &&
                                        value.type !== TypeTechnicalIndicator.COMPARETHEORETICALREAL && (
                                            <LineChart
                                                height={chartHeight}
                                                xAxis={[
                                                    {
                                                        data: value.values.map(currentValue => currentValue.label),
                                                        scaleType: 'point',
                                                        label: 'Date',
                                                    },
                                                ]}
                                                series={[
                                                    {
                                                        data: value.values.map(currentValue =>
                                                            Number.parseFloat(currentValue.value)
                                                        ),
                                                        label: getLabelChart(value.type),
                                                        color: getColorChart(value.type),
                                                    },
                                                ]}
                                            />
                                        )}
                                </Box>
                            ) : (
                                <Box className={'technical-indicators__emptyChart'}>
                                    <Typography>Aucun historique disponible</Typography>
                                </Box>
                            )}
                        </Box>
                    </Grid>
                );
            })}
        </Grid>
    );
}

function getTechnicalIndicatorMeta(type: TypeTechnicalIndicator): { label: string; icon: ReactNode } {
    switch (type) {
        case TypeTechnicalIndicator.COMPARETHEORETICALREAL:
            return {
                label: 'Comparaison entre le théorique facturé et le réel :',
                icon: <AnalyticsOutlinedIcon />,
            };
        case TypeTechnicalIndicator.EFFICIENCY:
            return {
                label: 'Rendement :',
                icon: <AccountBalanceIcon />,
            };
        case TypeTechnicalIndicator.EVOLUTIONBILLED:
            return {
                label: 'Évolution du facturé :',
                icon: <EuroIcon />,
            };
        case TypeTechnicalIndicator.BILLEDVOLUME:
            return {
                label: 'Volume facturé :',
                icon: <WaterDropIcon />,
            };
        case TypeTechnicalIndicator.INHABITANTS:
            return {
                label: 'Habitants :',
                icon: <HomeWorkIcon />,
            };
        case TypeTechnicalIndicator.LINEARNETWORK:
            return {
                label: 'Linéaire de réseau :',
                icon: <OpenInFullIcon sx={{ transform: 'rotate(90deg)' }} />,
            };
        case TypeTechnicalIndicator.PRODUCTVOLUME:
            return {
                label: 'Volume produit :',
                icon: <WaterDropIcon />,
            };
        case TypeTechnicalIndicator.RECIPEEXCLUDINGTAX:
            return {
                label: 'Recette HT - redevance comprise :',
                icon: <SignalCellularAltIcon />,
            };
        case TypeTechnicalIndicator.SUBSCRIBERS:
            return {
                label: 'Abonnés :',
                icon: <GroupsIcon />,
            };
        case TypeTechnicalIndicator.VOLUMECOUNTED:
            return {
                label: 'Volume comptabilisé :',
                icon: <WaterDropIcon />,
            };
        case TypeTechnicalIndicator.WATERCOURSELENGTH:
            return {
                label: "Linéaire de cours d'eau :",
                icon: <OpenInFullIcon />,
            };
        default:
            return {
                label: '',
                icon: <WaterDropIcon />,
            };
    }
}

function getComparisonChartData(values?: TechnicalIndicatorDto['values']) {
    const groupedValues: Record<string, { real: number; theoretical: number }> = {};

    values?.forEach(currentValue => {
        if (!groupedValues[currentValue.label]) {
            groupedValues[currentValue.label] = { real: 0, theoretical: 0 };
        }

        if (currentValue.key === 'theorique') {
            groupedValues[currentValue.label].theoretical = Number.parseFloat(currentValue.value);
            return;
        }

        groupedValues[currentValue.label].real = Number.parseFloat(currentValue.value);
    });

    const labels = Object.keys(groupedValues).sort((left, right) =>
        left.localeCompare(right, undefined, { numeric: true })
    );

    return {
        labels,
        real: labels.map(label => groupedValues[label].real),
        theoretical: labels.map(label => groupedValues[label].theoretical),
    };
}

/**
 * Méthode pour la couleur des graphiques.
 * @param type
 * @param key
 */
export function getColorChart(type: TypeTechnicalIndicator, key?: string) {
    switch (type) {
        case TypeTechnicalIndicator.COMPARETHEORETICALREAL:
            if (key === 'theorique') {
                return '#2456a6';
            }
            return '#6e90c7';
        case TypeTechnicalIndicator.EFFICIENCY:
            return '#3a70bb';
        case TypeTechnicalIndicator.EVOLUTIONBILLED:
            return '#1f2d3d';
        case TypeTechnicalIndicator.BILLEDVOLUME:
            return '#2f8a63';
        case TypeTechnicalIndicator.INHABITANTS:
            return '#7ebaa7';
        case TypeTechnicalIndicator.LINEARNETWORK:
            return '#2f6db0';
        case TypeTechnicalIndicator.PRODUCTVOLUME:
            return '#2f8a63';
        case TypeTechnicalIndicator.RECIPEEXCLUDINGTAX:
            return '#2f8a63';
        case TypeTechnicalIndicator.SUBSCRIBERS:
            return '#95be72';
        case TypeTechnicalIndicator.VOLUMECOUNTED:
            return '#bf6a33';
        case TypeTechnicalIndicator.WATERCOURSELENGTH:
            return '#7ebaa7';
        default:
            return '#3a70bb';
    }
}

/**
 * Méthode pour le libellé des graphiques.
 * @param type
 * @param key
 */
export function getLabelChart(type: TypeTechnicalIndicator, key?: string) {
    switch (type) {
        case TypeTechnicalIndicator.COMPARETHEORETICALREAL:
            if (key === 'theorique') {
                return 'Théorique';
            }
            return 'Réel';
        case TypeTechnicalIndicator.EFFICIENCY:
            return 'Rendement';
        case TypeTechnicalIndicator.EVOLUTIONBILLED:
            return 'Nombre de facture';
        case TypeTechnicalIndicator.BILLEDVOLUME:
            return 'Volume facturé';
        case TypeTechnicalIndicator.INHABITANTS:
            return 'Habitants';
        case TypeTechnicalIndicator.LINEARNETWORK:
            return 'Linéaire';
        case TypeTechnicalIndicator.PRODUCTVOLUME:
            return 'Volume';
        case TypeTechnicalIndicator.RECIPEEXCLUDINGTAX:
            return 'Recette';
        case TypeTechnicalIndicator.SUBSCRIBERS:
            return 'Abonnés';
        case TypeTechnicalIndicator.VOLUMECOUNTED:
            return 'Volume';
        case TypeTechnicalIndicator.WATERCOURSELENGTH:
            return "Linéaire de cours d'eau";
        default:
            return '';
    }
}
